import React, { useRef } from 'react';
import { Autocomplete, AutocompleteProps } from '@mui/material';

/**
 * MUI Autocomplete where, after typing to narrow the list, Tab picks the highlighted
 * option (the first match) and moves to the next field, the way QuickBooks does.
 * Tab without typing, or with the list closed, leaves the value alone.
 */
function TabSelectAutocompleteInner<T, DisableClearable extends boolean | undefined = false>(
  props: AutocompleteProps<T, false, DisableClearable, false>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { onChange, onHighlightChange, onInputChange, onKeyDown } = props;
  const highlighted = useRef<T | null>(null);
  const typed = useRef(false);
  return (
    <Autocomplete
      autoHighlight
      {...props}
      ref={ref}
      onHighlightChange={(event, option, reason) => {
        highlighted.current = option;
        onHighlightChange?.(event, option, reason);
      }}
      onInputChange={(event, value, reason) => {
        typed.current = reason === 'input';
        onInputChange?.(event, value, reason);
      }}
      onKeyDown={(event) => {
        const listOpen = (event.target as HTMLElement).getAttribute('aria-expanded') === 'true';
        if (event.key === 'Tab' && listOpen && typed.current && highlighted.current) {
          typed.current = false;
          onChange?.(event, highlighted.current as any, 'selectOption');
        }
        onKeyDown?.(event);
      }}
    />
  );
}

// forwardRef keeps react-hook-form's field ref working (it focuses a field with an error).
const TabSelectAutocomplete = React.forwardRef(TabSelectAutocompleteInner) as <
  T,
  DisableClearable extends boolean | undefined = false
>(
  props: AutocompleteProps<T, false, DisableClearable, false> & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof TabSelectAutocompleteInner>;

export default TabSelectAutocomplete;
